import { IDestinationFilter } from './destinationFilters/IDestinationFilter';
import { IGetUserStatisticsQueryResult } from '@/api/tracking/queries';
import { UUID } from '@/_core/utils/uuid/clientUUID';
import startCase from 'lodash/startCase';
import { TrackedEvent } from './trackedEvent';
import { redactPublishedAccess } from '@/_core/utils/publishedAccessRedaction';

export class TrackingClient {
    private writeKey: string;
    private stats: IGetUserStatisticsQueryResult;
    private destinationFilters: IDestinationFilter[];

    public constructor(writeKey: string, stats: IGetUserStatisticsQueryResult, destinationFilters: IDestinationFilter[]) {
        this.writeKey = writeKey;
        this.stats = stats;
        this.destinationFilters = destinationFilters;
    }

    public startTracking = () => {
        // @ts-ignore
        if (!window.analytics) {
            return;
        }

        if (this.stats.IsUserLoggedIn) {
            this.identifyUser();
        } else {
            this.identifyAnonymusUser();
        }

        if (this.stats.IsInWorkspaceContext) {
            this.identifyWorkspace();
        }
    };

    public registerPageVisit = async (pageName: string, properties: object = {}) => {
        // @ts-ignore
        if (!window.analytics) {
            return;
        }

        let commonProps = await this.getCommonEventProperties();

        const isPrivateLinkView = await this.isPrivateLinkSharedStorymapView();
        if (isPrivateLinkView) {
            const pageProps = await this.getPageProperties();
            commonProps = {
                ...commonProps,
                ...pageProps.context.page,
            };
        }

        properties = {
            ...commonProps,
            ...properties,
        };

        analytics.page(pageName, properties, this.getDestinationFilterProperties());
        this.registerEvent(TrackedEvent.PageViewed, { page: pageName });
    };

    public registerEvent = async (event: TrackedEvent, properties: object = {}) => {
        // @ts-ignore
        if (!window.analytics) {
            return;
        }

        const commonProps = await this.getCommonEventProperties();
        properties = {
            ...commonProps,
            ...properties,
        };

        const pageProps = await this.getPageProperties();
        const filterProps = this.getDestinationFilterProperties(event, properties);
        const options = {
            ...pageProps,
            ...filterProps,
        };

        const eventName = this.getEventName(event, properties);
        analytics.track(eventName, properties, options);
    };

    public registerVirtualPageVisit = async (path: string, pageName: string) => {
        // @ts-ignore
        if (!window.analytics) {
            return;
        }

        const redactedPath = redactPublishedAccess(path);
        const props = {
            path: redactedPath,
            url: window.location.origin + redactedPath,
        };

        analytics.page(pageName, props, this.getDestinationFilterProperties());
    };

    private identifyUser = async () => {
        const pageProps = await this.getPageProperties();
        const filterProps = this.getDestinationFilterProperties();
        const options = {
            ...pageProps,
            ...filterProps,
        };

        const onboardingProps = this.getOnboardingProperties();
        const props = {
            ...{
                name: this.stats.Name,
                email: this.stats.Email,
            },
            ...onboardingProps,
        };

        analytics.identify(this.stats.Id, props, options);
    };

    private identifyAnonymusUser = async () => {
        const pageProps = await this.getPageProperties();
        const filterProps = this.getDestinationFilterProperties();
        const options = {
            ...pageProps,
            ...filterProps,
        };

        const anonymusId = 'Anonymous User: ' + UUID.create();
        analytics.identify(anonymusId, {}, options);
    };

    private identifyWorkspace = async () => {
        const pageProps = await this.getPageProperties();
        const filterProps = this.getDestinationFilterProperties();
        const options = {
            ...pageProps,
            ...filterProps,
        };


        analytics.group(
            this.stats.Workspace.Id.toString(),
            {
                name: this.stats.Workspace.Name,
            },
            options
        );
    };

    private getCommonEventProperties: () => Promise<object> = async () => {
        let commonProps = {
            userId: this.stats.Id,
            userShortId: this.stats.ShortId,
            isUserLoggedIn: this.stats.IsUserLoggedIn,
            userName: this.stats.Name,
            userEmail: this.stats.Email,
            userEmailDomain: this.stats.EmailDomain,
            userIpAddress: this.stats.IpAddress,
            userSignedUpAt: this.stats.SignedUpAt,
            userLoginCount: this.stats.LoginCount,
            sessionId: this.stats.SessionId,
            userNumberOfWorkspacesWhereMember: this.stats.NumberOfWorkspacesWhereMember,
            userHighestGlobalWorkspaceRole: this.stats.HighestGlobalWorkspaceRole,
            userNumberOfNonTrialWorkspacesWhereMember: this.stats.NumberOfNonTrialWorkspacesWhereMember,
            userHighestNonTrialWorkspaceRole: this.stats.HighestNonTrialWorkspaceRole,
            userNumberOfStorymapsWhereCollaborator: this.stats.NumberOfStorymapsWhereCollaborator,
            userHighestGlobalStorymapRole: this.stats.HighestGlobalStorymapRole,
            userIsDomainControlled: this.stats.IsDomainControlled,
            userIsLoggedInViaSso: this.stats.IsLoggedInViaSso,
            isInWorkspaceContext: this.stats.IsInWorkspaceContext,
        };

        if (this.stats.Workspace != null) {
            commonProps = {
                ...commonProps,
                ...{
                    wsId: this.stats.Workspace.Id,
                    wsSubdomain: this.stats.Workspace.Subdomain,
                    wsName: this.stats.Workspace.Name,
                    wsCreatedAt: this.stats.Workspace.CreatedAt,
                    wsCreatedBy: this.stats.Workspace.CreatedBy,
                    wsRole: this.stats.Workspace.WorkspaceRole,
                    wsIsTrial: this.stats.Workspace.IsTrial,
                    wsIsExpiredTrial: this.stats.Workspace.IsExpiredTrial,
                    wsSubscriptionPlanId: this.stats.Workspace.SubscriptionPlanId,
                    wsSubscriptionSourcePlanId: this.stats.Workspace.SubscriptionSourcePlanId,
                    wsSubscriptionPlan: this.stats.Workspace.SubscriptionPlan,
                    wsIsSuspended: this.stats.Workspace.IsSuspended,
                    wsSubscriptionEditorCount: this.stats.Workspace.SubscriptionEditorCount,
                    wsNumberOfMembers: this.stats.Workspace.NumberOfMembers,
                    wsBillingCountry: this.stats.Workspace.BillingCountry,
                    wsLimitsAreExceed: initAppData.isInWsContext ? initAppData.currentWs.subscriptionLimitsAreExceed : false,
                },
            };
        }

        // const isInStorymapContext = await this.context.isInStorymapContext();
        // if (isInStorymapContext) {
        //     commonProps['userIsPrivateLinkSharedMapViewer'] = await this.isPrivateLinkSharedStorymapView();
        // }

        return commonProps;
    };

    private isPrivateLinkSharedStorymapView = async () => {
        // const isInStorymapContext = await this.context.isInStorymapContext();

        // if (isInStorymapContext) {
        //     const storymapContext = await this.context.getStorymapContext();
        //     return storymapContext.userRights.isPrivateLinkAccess;
        // }

        return false;
    };

    private getPageProperties: () => Promise<SegmentAnalytics.SegmentOpts> = async () => {
        const isPrivateLinkShared = await this.isPrivateLinkSharedStorymapView();
        return isPrivateLinkShared
            ? {
                context: {
                    page: {
                        path: redactPublishedAccess(window.location.pathname),
                        url: window.location.origin + redactPublishedAccess(window.location.pathname),
                        referrer: null,
                    },
                },
            }
            : {};
    };

    private getOnboardingProperties = () => {
        if (this.stats != null && this.stats.OnboardingInfo != null) {
            return this.stats.OnboardingInfo;
        }

        return {};
    };

    private getEventName = (event: TrackedEvent, properties: any): string => {
        let eventName = TrackedEvent[event];
        if (event == TrackedEvent.PageViewed) {
            eventName = properties.page + 'Viewed';
        }
        //return eventName.camelCaseToSentence();
        return startCase(eventName);
    };

    private getDestinationFilterProperties = (event: TrackedEvent = null, properties: any = null) => {
        const props = {
            integrations: {
                All: true,
            },
        };

        this.destinationFilters.forEach((x) => {
            if (event == null || properties == null || x.isEventExcluded(event, properties)) {
                //@ts-ignore
                props.integrations[x.destinationName] = false;
            }
        });

        return props;
    };
}
